import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FaBolt,
  FaChartLine,
  FaEye,
  FaPercent,
} from "react-icons/fa";
import { ShimmerThumbnail } from "react-shimmer-effects";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardCard from "../../../Components/Cards/DashboardCard";
import { useGetAnalyticsOverviewQuery } from "../../../Data/services/analyticsApi";
import { ErrorToast } from "../../../Hooks/useToast";
import styles from "../styles/Dashboard.module.scss";
import { BAR_COLOR, PIE_COLORS, extractData } from "../utils";
import ChartSkeleton from "./ChartSkeleton";

const RANGE_OPTIONS = ["7d", "28d", "90d"];
const PLATFORM_ORDER = ["Android", "iOS", "web"];
const PLATFORM_COLORS = {
  Android: "#0180F6",
  iOS: "#3EBF8F",
  web: "#FF5757",
};

const normalizePlatformLabel = (value) => {
  const key = String(value || "").trim().toLowerCase();
  if (key === "android") return "Android";
  if (key === "ios" || key === "iphone" || key === "ipad") return "iOS";
  if (key === "web" || key === "website" || key === "desktop") return "web";
  return String(value || "").trim() || "Unknown";
};

const formatChartDate = (value) => {
  if (!value || typeof value !== "string") return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const shortEventName = (name) => {
  if (!name) return "unknown";
  return name.length > 18 ? `${name.slice(0, 16)}…` : name;
};

export default function FirebaseAnalyticsSection() {
  const [range, setRange] = useState("28d");
  const {
    data: analyticsRes,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAnalyticsOverviewQuery(range, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isError) {
      ErrorToast(error?.data?.message || "Failed to load analytics");
    }
  }, [isError, error]);

  const payload = useMemo(() => extractData(analyticsRes) || {}, [analyticsRes]);

  const kpiTiles = useMemo(() => {
    const kpis =
      payload.kpis && typeof payload.kpis === "object" ? payload.kpis : {};
    return [
      {
        key: "sessions",
        text: "Sessions",
        number: Number(kpis.sessions) || 0,
        bg1: "#0A6BCB",
        bg2: "#3D9AED",
        icon: <FaChartLine color="#0A6BCB" />,
      },
      {
        key: "views",
        text: "Views",
        number: Number(kpis.screen_page_views) || 0,
        bg1: "#5B4BDB",
        bg2: "#8B7CF6",
        icon: <FaEye color="#5B4BDB" />,
      },
      {
        key: "events",
        text: "Events",
        number: Number(kpis.event_count) || 0,
        bg1: "#C45C00",
        bg2: "#E6A700",
        icon: <FaBolt color="#C45C00" />,
      },
      {
        key: "engagement",
        text: "Engagement",
        number: Math.round((Number(kpis.engagement_rate) || 0) * 100),
        bg1: "#0B8F6E",
        bg2: "#0180F6",
        icon: <FaPercent color="#0B8F6E" />,
      },
    ];
  }, [payload]);

  const usersChartData = useMemo(
    () =>
      (Array.isArray(payload.users_by_date) ? payload.users_by_date : []).map(
        (row) => ({
          date: row?.date,
          label: formatChartDate(row?.date),
          active_users: Number(row?.active_users) || 0,
        })
      ),
    [payload]
  );

  const eventsChartData = useMemo(
    () =>
      (Array.isArray(payload.top_events) ? payload.top_events : [])
        .slice(0, 10)
        .map((row) => {
          const eventName = row?.event_name || "unknown";
          return {
            event_name: eventName,
            short: shortEventName(eventName),
            event_count: Number(row?.event_count) || 0,
          };
        }),
    [payload]
  );

  const platformChartData = useMemo(() => {
    const rows = Array.isArray(payload.by_platform) ? payload.by_platform : [];
    const counts = {};
    rows.forEach((row) => {
      const label = normalizePlatformLabel(row?.platform);
      counts[label] = (counts[label] || 0) + (Number(row?.active_users) || 0);
    });
    const known = PLATFORM_ORDER.map((platform) => ({
      platform,
      active_users: counts[platform] || 0,
    }));
    const extras = Object.keys(counts)
      .filter((name) => !PLATFORM_ORDER.includes(name))
      .map((platform) => ({
        platform,
        active_users: counts[platform],
      }));
    return [...known, ...extras];
  }, [payload]);

  const showSkeleton = (isLoading || isFetching) && !payload.range;
  const rangeHint =
    payload.start_date && payload.end_date
      ? `${formatChartDate(payload.start_date)} – ${formatChartDate(
          payload.end_date
        )}`
      : "Product app users from GA4";

  return (
    <div className={`${styles.panel} mt-4`}>
      <div className={styles.panelHeaderRow}>
        <div>
          <h5 className={styles.panelTitle}>Firebase Analytics</h5>
          <p className={styles.panelHint}>{rangeHint}</p>
        </div>
        <div className={styles.headerActions}>
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={`${styles.chip} ${
                range === option ? styles.chipActive : ""
              }`}
              onClick={() => setRange(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <Row className={`${styles.matchRow} w-100 g-3`}>
        {showSkeleton
          ? Array.from({ length: 4 }).map((_, i) => (
              <Col key={i} xl={3} lg={3} md={6} sm={6}>
                <ShimmerThumbnail height={110} rounded />
              </Col>
            ))
          : kpiTiles.map((tile) => (
              <Col key={tile.key} xl={3} lg={3} md={6} sm={6}>
                <DashboardCard
                  compact
                  bg1={tile.bg1}
                  bg2={tile.bg2}
                  icon={tile.icon}
                  number={tile.number}
                  text={tile.text}
                />
              </Col>
            ))}
      </Row>

      <div className={`${styles.panelHeader} mt-4`}>
        <h5 className={styles.panelTitle}>Active users by day</h5>
      </div>
      {showSkeleton ? (
        <ChartSkeleton />
      ) : (
        <div className={styles.chartBox}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={usersChartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="active_users"
                name="Active users"
                stroke={BAR_COLOR}
                fill={BAR_COLOR}
                fillOpacity={0.18}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <Row className="w-100 mt-3 g-3">
        <Col lg={6} md={12}>
          <div className={styles.panelHeader}>
            <h5 className={styles.panelTitle}>Top events</h5>
            <p className={styles.panelHint}>Highest event counts in range</p>
          </div>
          {showSkeleton ? (
            <ChartSkeleton />
          ) : (
            <div className={styles.chartBox}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  layout="vertical"
                  data={eventsChartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="short"
                    width={110}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(value, _n, props) => [
                      value,
                      props?.payload?.event_name,
                    ]}
                  />
                  <Bar
                    dataKey="event_count"
                    fill={BAR_COLOR}
                    radius={[0, 6, 6, 0]}
                    name="Events"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Col>

        <Col lg={6} md={12}>
          <div className={styles.panelHeader}>
            <h5 className={styles.panelTitle}>By platform</h5>
            <p className={styles.panelHint}>Android · iOS · web</p>
          </div>
          {showSkeleton ? (
            <ChartSkeleton />
          ) : (
            <>
              <div className={styles.chartBox}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={platformChartData}
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar
                      dataKey="active_users"
                      name="Active users"
                      radius={[6, 6, 0, 0]}
                    >
                      {platformChartData.map((entry, index) => (
                        <Cell
                          key={entry.platform}
                          fill={
                            PLATFORM_COLORS[entry.platform] ||
                            PIE_COLORS[index % PIE_COLORS.length]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={styles.chipRow}>
                {platformChartData.map((row) => (
                  <span key={row.platform} className={styles.chip}>
                    {row.platform} · {row.active_users}
                  </span>
                ))}
              </div>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
}
